import { Modal } from "../ui/modal";
import styles from "../ui/confirm-dialog.module.css";

/**
 * *"Move the 2 later visits by the same number of days?"* — CH4 — and its
 * sibling for a cancellation or a no-show, *"Cancel the 2 later visits too?"*
 * (CH11, CH13). One dialog, because the three answers are the same shape.
 *
 * Asked **before** the move is sent, not after: moving visit 2 past visit 3 on
 * its own is refused (CH8), so asking afterwards would often never get the
 * chance. **Three answers, not two**, which is why this is not `ConfirmDialog`:
 * move them too, move only this one, or — closing it — move nothing. Escape
 * must mean "nothing happened", never "only this one".
 */
export function ShiftDialog({
  later,
  kind = "move",
  onAnswer,
}: {
  /** How many later visits of the plan are booked. */
  later: number;
  /** Moving this visit, or cancelling it / marking it a no-show. */
  kind?: "move" | "cancel";
  /** true = shift them, false = only this one, null = move nothing. */
  onAnswer: (answer: boolean | null) => void;
}): React.JSX.Element {
  const them = later === 1 ? "the next visit" : `the ${String(later)} later visits`;
  const title = kind === "move" ? "Move the rest of the plan?" : "Cancel the rest of the plan?";

  return (
    <Modal label={title} onClose={() => onAnswer(null)} testId="shift-dialog">
      <h2 className={styles.title}>{title}</h2>
      <div className={styles.body}>
        {kind === "move" ? (
          <p>
            This appointment is part of a treatment plan. Move {them} by the same number of days, so
            the gaps between visits stay as they are?
          </p>
        ) : (
          <p>
            This appointment is part of a treatment plan. Cancel {them} as well? They go back to
            waiting to be booked.
          </p>
        )}
      </div>

      <div className={styles.actions}>
        <button
          type="button"
          className={styles.cancel}
          onClick={() => onAnswer(false)}
          data-testid="shift-only-this"
        >
          Only this one
        </button>
        <button
          type="button"
          className={kind === "move" ? styles.confirm : styles.danger}
          onClick={() => onAnswer(true)}
          data-testid="shift-all"
        >
          {kind === "move" ? "Move" : "Cancel"} {later === 1 ? "it" : "them"} too
        </button>
      </div>
    </Modal>
  );
}
